import React from 'react';
import DodTermText from '../DodTermText';
import {
  AGENT_INSTALL_VENUES,
  agentInstallCommandNotes,
  agentInstallEnvBlurb,
  agentInstallEnvLabel,
  agentInstallScriptPath,
  agentNetworkGuide,
  commandForAgentInstall,
  defaultEnvForVenue,
  detectBrowserAgentEnv,
  venueForEnv,
  type AgentInstallEnvId,
  type AgentInstallVenueId,
} from '../agentInstallCommands';
import { useLocalAgentPresence } from '../hooks/useLocalAgentPresence';
import './WebsiteAgentInstallCard.css';

type InstallAdvice = {
  primary_venue?: AgentInstallVenueId | string;
  also_consider?: string[];
  avoid?: string[];
  reason_en?: string;
  reason_es?: string;
  default_env?: AgentInstallEnvId | string;
  recommended_env?: AgentInstallEnvId | string;
  recommended_env_label_en?: string;
  recommended_env_label_es?: string;
  recommended_env_why_en?: string;
  recommended_env_why_es?: string;
  action_plan?: {
    title_en?: string;
    title_es?: string;
    steps_en?: string[];
    steps_es?: string[];
  };
};

const normalizeVenue = (raw: string | undefined): AgentInstallVenueId | null => {
  const id = String(raw || '').trim();
  if (id === 'local' || id === 'cloud' || id === 'onprem') return id;
  if (id === 'network') return 'onprem';
  return null;
};

type HostingHint = {
  providers?: string[];
  cms?: string[];
  languages?: string[];
  cdns?: string[];
  hosting_class?: string;
  evidence?: string[];
  managed_edge?: boolean;
  note?: string;
  confidence?: number;
  install_advice?: InstallAdvice | null;
};

type Props = {
  language: 'en' | 'es';
  gatewayBaseUrl: string;
  accessToken: string;
  siteLabel?: string;
  planAllowsInstall: boolean;
  readOnly?: boolean;
  hostingHint?: HostingHint | null;
  /** Parent runs the homepage scan; hosting fingerprint lands in lastScan.hosting. */
  onLookupHosting?: () => void | Promise<void>;
  lookupBusy?: boolean;
  /** Live status from the parent scan (errors / progress). */
  lookupMessage?: string | null;
  /** Website currently selected in the console (shown on the lookup control). */
  lookupTargetLabel?: string;
  /** When true, sits inside the agent status card (no outer stack chrome). */
  embedded?: boolean;
  /** Increment to open the Install agent panel (e.g. from the Update chip). */
  installOpenSignal?: number;
  /** When true, label the toggle as the update path (same install command). */
  updateAvailable?: boolean;
};

const venueTitle = (id: string, es: boolean): string => {
  const normalized = normalizeVenue(id) || id;
  if (normalized === 'local') return es ? 'Este equipo' : 'This computer';
  if (normalized === 'cloud') return es ? 'Nube' : 'Cloud';
  if (normalized === 'onprem') return es ? 'Red de la empresa' : 'Company network';
  if (id === 'public') return es ? 'Solo escaneo público' : 'Public scan only';
  return id;
};

const hostingClassLabel = (value: string | undefined, es: boolean): string => {
  if (value === 'managed_edge_or_saas') {
    return es ? 'CDN / SaaS' : 'CDN / SaaS';
  }
  if (value === 'origin_visible') {
    return es ? 'Origen visible' : 'Origin visible';
  }
  return es ? 'Desconocido' : 'Unknown';
};

/**
 * Compact website-agent tools: hosting lookup button + optional install command.
 * Meant to sit under AgentRunBadges in one combined panel.
 */
export default function WebsiteAgentInstallCard({
  language,
  gatewayBaseUrl,
  accessToken,
  siteLabel = 'site-1',
  planAllowsInstall,
  readOnly = false,
  hostingHint = null,
  onLookupHosting,
  lookupBusy = false,
  lookupMessage = null,
  lookupTargetLabel = '',
  embedded = false,
  installOpenSignal = 0,
  updateAvailable = false,
}: Props): React.ReactElement {
  const gateway = gatewayBaseUrl.replace(/\/$/, '');
  const label = String(siteLabel || 'site-1').trim() || 'site-1';
  const es = language === 'es';
  const [venue, setVenue] = React.useState<AgentInstallVenueId>('local');
  const [env, setEnv] = React.useState<AgentInstallEnvId>('laptop');
  const [token, setToken] = React.useState('');
  const [tokenDraft, setTokenDraft] = React.useState('');
  const [tokenPath, setTokenPath] = React.useState<'ask' | 'reuse' | 'mint' | 'ready'>('ask');
  const [busy, setBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');
  const [installOpen, setInstallOpen] = React.useState(false);
  const [hostingOpen, setHostingOpen] = React.useState(false);
  const [showNetwork, setShowNetwork] = React.useState(false);
  const adviceApplied = React.useRef(false);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const localPresence = useLocalAgentPresence(Boolean(planAllowsInstall && accessToken));

  React.useEffect(() => {
    if (!installOpenSignal || !planAllowsInstall) return;
    setInstallOpen(true);
    setHostingOpen(false);
    window.requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [installOpenSignal, planAllowsInstall]);

  React.useEffect(() => {
    if (lookupBusy) setHostingOpen(true);
  }, [lookupBusy]);

  const tokenValue = token || '';
  const network = agentNetworkGuide(gateway, language);
  const activeVenue = AGENT_INSTALL_VENUES.find((row) => row.id === venue) || AGENT_INSTALL_VENUES[0];
  const command = commandForAgentInstall(env, {
    gateway,
    token: tokenValue,
    label,
    language,
  });
  const hasToken = Boolean(token);
  const advice = hostingHint?.install_advice || null;

  // Prefer This computer by default. CloudFront → SSM is an opt-in chip, not an auto-switch.
  // Never re-apply after the operator picks a venue (adviceApplied).
  React.useEffect(() => {
    if (adviceApplied.current) return;
    if (localPresence.status === 'checking') return;
    adviceApplied.current = true;
    if (localPresence.status === 'found' || updateAvailable) {
      setVenue('local');
      setEnv(detectBrowserAgentEnv());
      return;
    }
    setVenue('local');
    setEnv(detectBrowserAgentEnv());
  }, [
    advice?.primary_venue,
    localPresence.status,
    updateAvailable,
  ]);

  // Force This computer when an update is due or this Mac already has the agent —
  // even if the operator previously opened Cloud advice.
  React.useEffect(() => {
    if (localPresence.status === 'found' || updateAvailable) {
      setVenue('local');
      setEnv(detectBrowserAgentEnv());
    }
  }, [localPresence.status, updateAvailable]);

  const recommendedEnv = String(advice?.recommended_env || advice?.default_env || '').trim();
  const recommendedWhy = es
    ? (advice?.recommended_env_why_es || advice?.recommended_env_why_en || '')
    : (advice?.recommended_env_why_en || advice?.recommended_env_why_es || '');
  const recommendedEnvLabel = es
    ? (advice?.recommended_env_label_es || advice?.recommended_env_label_en || recommendedEnv)
    : (advice?.recommended_env_label_en || advice?.recommended_env_label_es || recommendedEnv);

  const pickVenue = (next: AgentInstallVenueId) => {
    adviceApplied.current = true;
    setVenue(next);
    setEnv(defaultEnvForVenue(next));
    setCopied(false);
  };

  const pickEnv = (next: AgentInstallEnvId) => {
    adviceApplied.current = true;
    setEnv(next);
    const nextVenue = venueForEnv(next);
    if (nextVenue !== 'public') setVenue(nextVenue);
    setCopied(false);
  };

  const createToken = async (): Promise<string | null> => {
    if (readOnly || !accessToken || busy) return token || null;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const response = await fetch(`${gateway}/api/v1/agent/install-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ label }),
      });
      const body = await response.json() as { token?: string; detail?: string };
      if (!response.ok || !body.token) {
        setError(typeof body.detail === 'string' ? body.detail : (es ? 'No se pudo crear el token.' : 'Could not create the token.'));
        return null;
      }
      setToken(body.token);
      setMessage(es ? 'Token listo — va en el comando.' : 'Token ready — baked into the command.');
      return body.token;
    } catch {
      setError(es ? 'No se pudo crear el token.' : 'Could not create the token.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const resolveInstallEnv = (): AgentInstallEnvId => {
    // Update / local agent → always This computer for this browser OS.
    if (updateAvailable || localPresence.status === 'found' || venue === 'local') {
      return detectBrowserAgentEnv();
    }
    return env;
  };

  const copyCommand = async (overrideToken?: string) => {
    const useToken = String(overrideToken || tokenValue || '').trim();
    // Never copy a placeholder — prod returns 401 for unknown/revoked ncagent_ tokens.
    if (!useToken || useToken.includes('…') || useToken.includes('...') || useToken === 'ncagent_') {
      setError(es
        ? 'Genere o pegue un token ncagent_ completo arriba. Un token incompleto da HTTP 401.'
        : 'Generate or paste a full ncagent_ token above. An incomplete token returns HTTP 401.');
      return;
    }
    let useEnv = resolveInstallEnv();
    if (useEnv !== env) {
      setEnv(useEnv);
      setVenue(venueForEnv(useEnv) === 'public' ? 'local' : venueForEnv(useEnv));
    }
    // Preflight install scripts only (small). Avoids paste → zsh errors then curl 403/401.
    const scriptPath = agentInstallScriptPath(useEnv);
    const shouldProbe = scriptPath === '/api/v1/agent/install.sh'
      || scriptPath === '/api/v1/agent/install.ps1'
      || scriptPath === '/api/v1/agent/install-aws-ssm.sh'
      || scriptPath === '/api/v1/agent/install-aws-oneclick.sh';
    if (shouldProbe && scriptPath) {
      try {
        const probe = await fetch(`${gateway}${scriptPath}`, {
          method: 'GET',
          headers: { Authorization: `Bearer ${useToken}` },
        });
        // Drain body so the connection can close; we only care about status.
        await probe.arrayBuffer().catch(() => undefined);
        if (probe.status === 401 || probe.status === 403) {
          // AWS helpers often 403 on older gateways — fall back to this Mac.
          if (useEnv === 'aws_ssm' || useEnv === 'aws_oneclick') {
            useEnv = detectBrowserAgentEnv();
            setEnv(useEnv);
            setVenue('local');
            setMessage(es
              ? 'AWS no está permitido con este token. Usando instalación en este Mac.'
              : 'AWS install is not allowed with this token. Using install on this Mac.');
          } else {
            setError(es
              ? `El gateway rechazó el instalador (${probe.status}). Genere un token nuevo e intente otra vez.`
              : `Gateway refused the installer (${probe.status}). Generate a new token and retry.`);
            return;
          }
        } else if (probe.status === 404 && (useEnv === 'aws_ssm' || useEnv === 'aws_oneclick')) {
          useEnv = detectBrowserAgentEnv();
          setEnv(useEnv);
          setVenue('local');
          setMessage(es
            ? 'Instalador AWS no está en este gateway. Usando instalación en este Mac.'
            : 'AWS helper not on this gateway. Using install on this Mac.');
        } else if (!probe.ok) {
          setError(es
            ? `No se pudo verificar el instalador (HTTP ${probe.status}).`
            : `Could not verify the installer (HTTP ${probe.status}).`);
          return;
        }
      } catch {
        setError(es
          ? 'No hay red hacia el gateway. Compruebe HTTPS y vuelva a intentar.'
          : 'Cannot reach the gateway. Check HTTPS and retry.');
        return;
      }
    }
    const text = commandForAgentInstall(useEnv, {
      gateway,
      token: useToken,
      label,
      language,
    });
    // Defense in depth: never put # comments on the clipboard (zsh NOMATCH).
    const paste = text
      .split('\n')
      .filter((line) => {
        const trimmed = line.trim();
        return trimmed && !trimmed.startsWith('#');
      })
      .join('\n');
    try {
      await navigator.clipboard.writeText(paste);
      setCopied(true);
      const local = useEnv === 'laptop' || useEnv === 'windows' || useEnv === 'onprem';
      if (useEnv === 'aws_oneclick' || useEnv === 'aws_ssm') {
        setMessage(es
          ? 'Copiado. El script detecta perfil/región del AWS CLI local; si SSO caducó, le dirá aws sso login --profile …. No instala en este Mac.'
          : 'Copied. The script detects profile/region from your local AWS CLI; if SSO expired it prints aws sso login --profile …. Does not install on this Mac.');
      } else {
        setMessage(es
          ? (local
            ? 'Comando verificado y copiado. Péguelo en Terminal — instala el agente en ESTE Mac y arranca `run`.'
            : 'Comando verificado y copiado. Péguelo en la terminal (este método no instala en este Mac).')
          : (local
            ? 'Command verified and copied. Paste in Terminal — installs the agent on THIS Mac and starts `run`.'
            : 'Command verified and copied. Paste in a terminal (this method does not install on this Mac).'));
      }
    } catch {
      setError(es ? 'No se pudo copiar.' : 'Could not copy.');
    }
  };

  const applyExistingToken = () => {
    const raw = String(tokenDraft || '').trim();
    if (!raw.startsWith('ncagent_') || raw.length < 20 || raw.includes('…') || raw.includes('...')) {
      setError(es
        ? 'Pegue un token completo que empiece por ncagent_ (sin puntos suspensivos).'
        : 'Paste a full token that starts with ncagent_ (no ellipsis).');
      return;
    }
    setToken(raw);
    setTokenPath('ready');
    setError('');
    setMessage(es ? 'Token reutilizado. Copie el comando abajo.' : 'Token reused. Copy the command below.');
  };

  const prepareAndCopy = async () => {
    if (env === 'public') return;
    let nextToken = token;
    if (!nextToken) {
      const minted = await createToken();
      if (!minted) return;
      nextToken = minted;
      setTokenPath('ready');
    }
    await copyCommand(nextToken);
  };

  const hostingResult = hostingHint ? (
    <ul className="website-hosting-lookup__chips" aria-label={es ? 'Hosting detectado' : 'Detected hosting'}>
      {(hostingHint.cms || []).length ? <li>CMS: {(hostingHint.cms || []).join(', ')}</li> : null}
      {(hostingHint.languages || []).length ? <li>{es ? 'Lenguaje' : 'Language'}: {(hostingHint.languages || []).join(', ')}</li> : null}
      {(hostingHint.cdns || hostingHint.providers || []).length ? (
        <li>CDN: {(hostingHint.cdns || hostingHint.providers || []).join(', ')}</li>
      ) : null}
      <li>{es ? 'Clase' : 'Class'}: {hostingClassLabel(hostingHint.hosting_class, es)}</li>
    </ul>
  ) : null;

  const adviceLine = es
    ? (advice?.reason_es || advice?.reason_en || '')
    : (advice?.reason_en || advice?.reason_es || '');
  const hostingAdvice = advice && adviceLine ? (
    <div className="website-hosting-lookup__advice" role="status">
      <p className="website-hosting-lookup__advice-line">{adviceLine}</p>
      {advice.primary_venue && planAllowsInstall && recommendedEnv && recommendedEnv !== 'public' ? (
        <button
          type="button"
          className="website-hosting-lookup__advice-link"
          onClick={() => {
            const id = normalizeVenue(String(advice.primary_venue || ''));
            if (id) {
              pickVenue(id);
              const envHint = String(advice.recommended_env || advice.default_env || '').trim() as AgentInstallEnvId;
              if (envHint && envHint !== 'public') setEnv(envHint);
              setInstallOpen(true);
              setHostingOpen(false);
            }
          }}
        >
          {es
            ? `Instalar en ${venueTitle(String(advice.primary_venue), true)}${recommendedEnvLabel ? ` · ${recommendedEnvLabel}` : ''}`
            : `Install on ${venueTitle(String(advice.primary_venue), false)}${recommendedEnvLabel ? ` · ${recommendedEnvLabel}` : ''}`}
        </button>
      ) : null}
    </div>
  ) : null;

  const targetHint = String(lookupTargetLabel || label || '').trim();
  const lookupIdleHint = targetHint
    ? (es
      ? ('Sitio del selector: ' + targetHint + ' (no hace falta escribir la URL).')
      : ('From Website above: ' + targetHint + ' (no URL field to fill).'))
    : (es ? 'Elija un sitio arriba, luego busque hosting.' : 'Pick a website above, then look up hosting.');
  const lookupBusyHint = es
    ? ('Buscando hosting de ' + (targetHint || 'este sitio') + '…')
    : ('Looking up hosting for ' + (targetHint || 'this site') + '…');
  const lookupReadingHint = es
    ? ('NextCompli está leyendo ' + (targetHint || 'el sitio') + '…')
    : ('NextCompli is reading ' + (targetHint || 'the site') + '…');
  // Ignore stale "Enter the site URL" from an earlier failed lookup once the Website picker has a host.
  const staleEnterUrl = /enter the site url|escriba la url del sitio|pick a website above.*look up hosting/i.test(
    String(lookupMessage || ''),
  );
  const liveLookupMessage = lookupMessage && !(targetHint && staleEnterUrl) ? lookupMessage : null;

  const hostingSummary = hostingHint
    ? [
      (hostingHint.cdns || hostingHint.providers || [])[0]
        ? `CDN ${(hostingHint.cdns || hostingHint.providers || [])[0]}`
        : '',
      hostingClassLabel(hostingHint.hosting_class, es),
    ].filter(Boolean).join(' · ')
    : (es ? 'Sin búsqueda aún' : 'Not looked up yet');

  const runLookup = () => {
    adviceApplied.current = false;
    setHostingOpen(true);
    void onLookupHosting?.();
  };

  const primaryCopyLabel = busy
    ? (es ? 'Preparando…' : 'Preparing…')
    : hasToken
      ? (es ? 'Copiar comando' : 'Copy command')
      : (es ? 'Generar token y copiar' : 'Generate token & copy');

  const hostingPanel = (
    <div className={`website-agent-accordion${hostingOpen ? ' is-open' : ''}`}>
      <div className="website-agent-accordion__bar">
        <button
          type="button"
          className="website-agent-accordion__trigger"
          aria-expanded={hostingOpen}
          aria-controls="website-agent-hosting-panel"
          onClick={() => setHostingOpen((current) => !current)}
        >
          <span className="website-agent-accordion__chevron" aria-hidden="true" />
          <span className="website-agent-accordion__titles">
            <strong>{es ? 'Hosting del sitio' : 'Site hosting'}</strong>
            <span className="website-agent-accordion__summary">{hostingSummary}</span>
          </span>
        </button>
        <button
          type="button"
          className={lookupBusy
            ? 'dashboard-btn website-hosting-lookup__btn is-busy'
            : 'dashboard-btn website-hosting-lookup__btn'}
          disabled={Boolean(lookupBusy || readOnly || !onLookupHosting)}
          onClick={(event) => {
            event.stopPropagation();
            runLookup();
          }}
        >
          {lookupBusy ? <span className="website-hosting-lookup__spinner" aria-hidden="true" /> : null}
          {lookupBusy
            ? (es ? 'Buscando…' : 'Looking up…')
            : hostingHint
              ? (es ? 'Volver a buscar' : 'Look up again')
              : (es ? 'Buscar hosting' : 'Look up hosting')}
        </button>
      </div>
      {hostingOpen ? (
        <div
          id="website-agent-hosting-panel"
          className="website-agent-accordion__body"
          role="region"
          aria-label={es ? 'Detalle de hosting' : 'Hosting details'}
        >
          <p className="website-hosting-lookup__target" role="status">
            {lookupBusy ? lookupBusyHint : lookupIdleHint}
          </p>
          <div className="website-hosting-lookup__results" aria-live="polite">
            {lookupBusy ? (
              <p className="website-hosting-lookup__status is-busy" role="status">
                <span className="website-hosting-lookup__spinner" aria-hidden="true" />
                {lookupReadingHint}
              </p>
            ) : null}
            {!lookupBusy && liveLookupMessage ? (
              <p className="website-hosting-lookup__status" role="status">{liveLookupMessage}</p>
            ) : null}
            {!lookupBusy ? hostingResult : null}
            {!lookupBusy ? hostingAdvice : null}
            {!lookupBusy && !hostingHint && !liveLookupMessage ? (
              <p className="website-hosting-lookup__status">
                {es
                  ? 'Pulse Buscar hosting para leer cabeceras y CDN del sitio seleccionado.'
                  : 'Click Look up hosting to read headers and CDN for the selected site.'}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );

  if (!planAllowsInstall) {
    return (
      <section
        className={`website-agent-install-stack${embedded ? ' is-embedded' : ''}`}
        aria-label={es ? 'Agente del sitio' : 'Website agent'}
      >
        {hostingPanel}
        <p className="website-agent-install__core">
          <DodTermText
            language={language}
            seen={new Set()}
            text={es
              ? 'CompliCore escanea el sitio público. Instalar el agente es CompliAdvantage.'
              : 'CompliCore scans the public site. Installing the agent is CompliAdvantage.'}
          />
        </p>
      </section>
    );
  }

  return (
    <div
      ref={panelRef}
      className={`website-agent-install-stack${embedded ? ' is-embedded' : ''}`}
    >
      {hostingPanel}

      <div className={`website-agent-accordion website-agent-accordion--install${installOpen ? ' is-open' : ''}`}>
        <button
          type="button"
          className={`website-agent-install__cta${installOpen ? ' is-open' : ''}`}
          aria-expanded={installOpen}
          aria-controls="website-agent-install-panel"
          onClick={() => setInstallOpen((current) => !current)}
        >
          <span className="website-agent-install__cta-main">
            <span className="website-agent-accordion__chevron" aria-hidden="true" />
            <span>
              <strong>{es ? 'Instalar agente' : 'Install agent'}</strong>
              <span className="website-agent-install__cta-sub">
                {localPresence.status === 'found'
                  ? (es
                    ? `Ya en este Mac${localPresence.hostname ? ` · ${localPresence.hostname}` : ''} — use ↑ Actualizar arriba para el paquete.`
                    : `Already on this Mac${localPresence.hostname ? ` · ${localPresence.hostname}` : ''} — use ↑ Update above for the package.`)
                  : (es
                    ? 'Este equipo, nube o red — un comando pegable'
                    : 'This computer, cloud, or network — one pasteable command')}
              </span>
            </span>
          </span>
          <span className="website-agent-install__cta-action" aria-hidden="true">
            {installOpen ? (es ? 'Ocultar' : 'Hide') : (es ? 'Abrir' : 'Open')}
          </span>
        </button>

        {installOpen ? (
          <div
            id="website-agent-install-panel"
            className="website-agent-accordion__body website-agent-install is-panel"
            role="region"
            aria-label={es ? 'Instalar agente' : 'Install agent'}
          >
            <div className="website-agent-install__flow">
              <div
                id="website-agent-venue"
                className="website-agent-install__venues is-compact"
                role="tablist"
                aria-label={es ? 'Dónde instalar' : 'Where to install'}
              >
                {AGENT_INSTALL_VENUES.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    role="tab"
                    aria-selected={venue === row.id}
                    className={`website-agent-install__venue${venue === row.id ? ' is-active' : ''}${advice?.primary_venue === row.id ? ' is-recommended' : ''}`}
                    onClick={() => pickVenue(row.id)}
                  >
                    <strong>{es ? row.label_es : row.label_en}</strong>
                  </button>
                ))}
              </div>

              <div className="website-agent-install__flavor" role="tablist" aria-label={es ? 'Método' : 'Method'}>
                {activeVenue.envs.map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={env === id}
                    className={`${env === id ? 'is-active' : ''}${recommendedEnv === id ? ' is-recommended' : ''}`.trim() || undefined}
                    onClick={() => pickEnv(id)}
                  >
                    {agentInstallEnvLabel(id, language)}
                  </button>
                ))}
                <button
                  type="button"
                  role="tab"
                  aria-selected={env === 'public'}
                  className={env === 'public' ? 'is-active' : undefined}
                  onClick={() => pickEnv('public')}
                >
                  {agentInstallEnvLabel('public', language)}
                </button>
              </div>

              <p className="website-agent-install__blurb" role="status">
                {agentInstallEnvBlurb(env, language)}
                {recommendedEnv === env && recommendedWhy ? ` ${recommendedWhy}` : null}
              </p>

              {env !== 'public' && !readOnly ? (
                <div className="website-agent-install__actions">
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn-primary website-agent-install__primary"
                    disabled={busy}
                    onClick={() => void prepareAndCopy()}
                  >
                    {primaryCopyLabel}
                  </button>
                  <button
                    type="button"
                    className="website-agent-install__text-btn"
                    disabled={busy}
                    onClick={() => {
                      setTokenPath((current) => (current === 'reuse' ? 'ask' : 'reuse'));
                      setError('');
                    }}
                  >
                    {es ? 'Pegar token existente' : 'Paste existing token'}
                  </button>
                  <button
                    type="button"
                    className="website-agent-install__text-btn"
                    aria-expanded={showNetwork}
                    onClick={() => setShowNetwork((value) => !value)}
                  >
                    {es ? 'Red' : 'Network'}
                  </button>
                  <a
                    className="website-agent-install__text-btn"
                    href="/console/agent"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {es ? 'Flota' : 'Fleet'}
                  </a>
                </div>
              ) : null}

              {tokenPath === 'reuse' && env !== 'public' && !readOnly ? (
                <div className="website-agent-install__token-reuse">
                  <label htmlFor="website-agent-token-reuse">
                    {es ? 'Token ncagent_' : 'ncagent_ token'}
                  </label>
                  <div className="website-agent-install__token-reuse-row">
                    <input
                      id="website-agent-token-reuse"
                      type="text"
                      autoComplete="off"
                      spellCheck={false}
                      value={tokenDraft}
                      onChange={(event) => setTokenDraft(event.target.value)}
                      placeholder="ncagent_…"
                    />
                    <button type="button" className="dashboard-btn" onClick={applyExistingToken}>
                      {es ? 'Usar' : 'Use'}
                    </button>
                  </div>
                </div>
              ) : null}

              {hasToken && tokenPath === 'ready' ? (
                <p className="website-agent-install__token-ready" role="status">
                  {es
                    ? `Token ${token.slice(0, 12)}… incluido en el comando.`
                    : `Token ${token.slice(0, 12)}… baked into the command.`}
                </p>
              ) : null}

              {showNetwork ? (
                <div className="website-agent-install__network" role="region" aria-label={network.title}>
                  <strong>{network.title}</strong>
                  <ul>
                    {network.lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {env !== 'public' ? (
                <div className="website-agent-install__cmd-wrap">
                  <div className="website-agent-install__cmd-head">
                    <span>{es ? 'Comando' : 'Command'}</span>
                    {copied ? <span className="website-agent-install__copied">{es ? 'Copiado' : 'Copied'}</span> : null}
                  </div>
                  {agentInstallCommandNotes(env, language).slice(0, 2).map((note) => (
                    <p key={note} className="website-agent-install__cmd-note">{note}</p>
                  ))}
                  {hasToken ? (
                    <pre className="website-agent-install__cmd" tabIndex={0}>{command}</pre>
                  ) : (
                    <p className="website-agent-install__cmd-wait" role="status">
                      {es
                        ? 'Pulse Generar token y copiar — el comando completo aparece aquí.'
                        : 'Click Generate token & copy — the full command appears here.'}
                    </p>
                  )}
                </div>
              ) : (
                <pre className="website-agent-install__cmd" tabIndex={0}>{command}</pre>
              )}

              {message ? <p className="website-agent-install__ok" role="status">{message}</p> : null}
              {error ? <p className="website-agent-install__error" role="alert">{error}</p> : null}

              <p
                className={`website-hosting-lookup__local is-${localPresence.status}`}
                role="status"
              >
                {localPresence.status === 'checking'
                  ? (es ? 'Comprobando este Mac…' : 'Checking this Mac…')
                  : localPresence.status === 'found'
                    ? (es
                      ? `Agente local${localPresence.hostname ? ` · ${localPresence.hostname}` : ''} — use ↑ Actualizar arriba.`
                      : `Local agent${localPresence.hostname ? ` · ${localPresence.hostname}` : ''} — use ↑ Update above.`)
                    : (es
                      ? 'Sin agente en este Mac. Copie el comando de Este equipo.'
                      : 'No agent on this Mac. Copy the This computer command.')}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
